<script lang="ts" setup>
import { computed, onMounted, ref, watch, nextTick } from 'vue';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import { useFormNavigation } from '../hooks/useFormNavigation';
import { useFormStore } from '../store/useFormStore';
import { denormalizeFormData } from '../types/repeatRules';
import { extractAllFields } from '../types/formUtils';
import { createZodSchema } from '../types/validation';
import { useFormPageValidation } from './core/hooks/useFormPageValidation';
import { useFormNavigationActions } from './core/hooks/useFormNavigationActions';
import { useFormSubmission } from './core/hooks/useFormSubmission';
import { useFormReset } from './core/hooks/useFormReset';
import FormEngineContent from './core/FormEngineContent.vue';
import type { FormStructure } from '../types/formTypeStructure';

interface Props {
  form: FormStructure;
  formId: string;
  currentLang?: string;
  onSubmit: (data: Record<string, any>) => void;
  submitMethod?: (data: Record<string, any>) => Promise<any>;
  initialData?: Record<string, any>;
  previewBeforeSubmission?: boolean;
  /**
   * Mode de pagination (optionnel, peut être défini dans form.layoutOptions.paginationMode)
   * - "byFields" (défaut) = par nombre de champs
   * - "bySection" = une section = une page
   * La prop a priorité sur form.layoutOptions.
   */
  paginationMode?: "byFields" | "bySection";
}

const props = defineProps<Props>();
const emit = defineEmits(['submit']);

const currentLang = computed(() => props.currentLang || 'fr');
const formStore = useFormStore();

// Déterminer le mode effectif : prop > form.layoutOptions > défaut
const paginationMode = computed<'byFields' | 'bySection'>(
  () => props.paginationMode ?? props.form.layoutOptions?.paginationMode ?? 'byFields'
);

const navigation = useFormNavigation({
  form: props.form,
  paginationMode: paginationMode.value,
  hasPreviewSection: props.previewBeforeSubmission ?? false
});
const allFields = extractAllFields(props.form);
const schema = createZodSchema(allFields);

// Créer les valeurs initiales vides
const createEmptyValues = () => Object.fromEntries(
  allFields.map((field) => [
    field.fieldName,
    field.fieldType === 'checkbox' ? [] : ''
  ])
);

// Créer les valeurs initiales avec les données sauvegardées
const createInitialValues = () => {
  // Priorité : initialData > store > response.responseValue > valeur par défaut
  const savedValues = formStore.getFormValues(props.formId) ?? {};
  
  return Object.fromEntries(
    allFields.map((field) => {
      // Si initialData est fourni, l'utiliser en priorité
      if (props.initialData && props.initialData[field.fieldName] !== undefined) {
        return [field.fieldName, props.initialData[field.fieldName]];
      }
      // Sinon, utiliser les valeurs du store
      if (savedValues[field.fieldName] !== undefined) {
        return [field.fieldName, savedValues[field.fieldName]];
      }
      // Sinon, utiliser response.responseValue
      if (field.response?.responseValue !== undefined) {
        return [field.fieldName, field.response.responseValue];
      }
      // Sinon, valeur par défaut selon le type
      return [field.fieldName, field.fieldType === 'checkbox' ? [] : ''];
    })
  );
};

// Initialiser Vee-Validate
const { values, setFieldValue, resetForm, validate, meta, errors } = useForm({
  validationSchema: toTypedSchema(schema),
  initialValues: createInitialValues()
});

// Hooks de validation et navigation
const { validatePage, isValidating: isValidatingValidation } = useFormPageValidation(
  navigation,
  values,
  { validate, meta, errors },
  currentLang.value,
  paginationMode.value
);

// Fonction pour synchroniser les données du store avec le formulaire
async function syncFormWithStore() {
  const storeValues = formStore.getFormValues(props.formId);
  
  if (storeValues && Object.keys(storeValues).length > 0) {
    // Dénormaliser les données du store pour les restaurer dans le formulaire
    const denormalizedValues = denormalizeFormData(storeValues, props.form.sections);
    
    // Créer un objet avec toutes les valeurs (actuelles + store)
    const mergedValues = { ...values };
    let updatedCount = 0;
    
    // Mettre à jour les valeurs du formulaire avec celles du store
    Object.entries(denormalizedValues).forEach(([fieldName, value]) => {
      if (value !== undefined && value !== null) {
        mergedValues[fieldName] = value;
        updatedCount++;
      }
    });
    
    if (updatedCount > 0) {
      await nextTick();
      resetForm({ values: mergedValues });
    }
  }
}

// Hooks de soumission
const { handleSubmitWithValidation, isValidating: isValidatingSubmit } = useFormSubmission(
  values,
  props.form,
  validatePage,
  props.onSubmit,
  props.submitMethod
);

// Hooks de navigation
const { handleNext, handlePrevious, isValidating: isValidatingNav } = useFormNavigationActions(
  navigation,
  validatePage,
  syncFormWithStore,
  props.form,              // 🆕 Passer le formulaire
  values,                  // 🆕 Passer les valeurs du formulaire
  paginationMode.value,    // 🆕 Passer le mode de pagination
  handleSubmitWithValidation // 🆕 Passer la fonction de soumission
);

const { handleClearAll } = useFormReset(
  resetForm,
  allFields,
  props.formId,
  navigation
);

// État de validation combiné
const isValidating = computed(() => 
  isValidatingValidation.value || isValidatingNav.value || isValidatingSubmit.value
);

// Watch pour réagir aux changements de initialData
watch(() => props.initialData, (newInitialData) => {
  if (newInitialData && Object.keys(newInitialData).length > 0) {
    const mergedValues = { ...values, ...newInitialData };
    resetForm({ values: mergedValues });
  }
}, { deep: true, immediate: false });

// Vérifier au chargement si le formulaire a été supprimé
onMounted(() => {
  const isFormCleared = localStorage.getItem('form-cleared-' + props.formId) === 'true';
  if (isFormCleared) {
    // Forcer les valeurs vides dans le formulaire si il a été supprimé
    const emptyValues = createEmptyValues();
    resetForm({ values: emptyValues });
  } else {
    // Si initialData est fourni, l'utiliser en priorité
    if (props.initialData && Object.keys(props.initialData).length > 0) {
      const mergedValues = { ...values, ...props.initialData };
      resetForm({ values: mergedValues });
    } else {
      // Sinon, synchroniser les données du store avec le formulaire au chargement
      syncFormWithStore();
    }
  }

  // Réhydratation initiale : marquer tous les champs non vides comme "touched"
  // pour que la validation visuelle reflète l'état chargé
  Object.entries(values).forEach(([fieldName, value]) => {
    if (value !== undefined && value !== '' && value !== null && 
        !(Array.isArray(value) && value.length === 0)) {
      // Note: Vee-Validate gère automatiquement les champs touchés lors de la validation
      // On peut utiliser setFieldTouched si nécessaire, mais ce n'est pas toujours disponible
    }
  });
});

// Fonction pour nettoyer le marqueur de suppression quand l'utilisateur saisit vraiment
function clearSuppressionMarker(newValue?: any) {
  const isFormCleared = localStorage.getItem('form-cleared-' + props.formId) === 'true';
  if (isFormCleared) {
    const isRealInput = newValue !== '' && newValue !== null && newValue !== undefined && 
                       !(Array.isArray(newValue) && newValue.length === 0);
    
    if (isRealInput) {
      localStorage.removeItem('form-cleared-' + props.formId);
    }
  }
}

// Gestionnaire de changement de champ
function handleFieldChange(fieldName: string, value: any) {
  clearSuppressionMarker(value);
  setFieldValue(fieldName, value);
}
</script>

<template>
  <FormEngineContent
    :form="form"
    :formId="formId"
    :currentLang="currentLang"
    :navigation="navigation"
    :formValues="values"
    :handleFieldChange="handleFieldChange"
    :onClearAll="handleClearAll"
    :handleNext="handleNext"
    :handlePrevious="handlePrevious"
    :handleFinalSubmit="handleSubmitWithValidation"
    :isValidating="isValidating"
  />
</template>
